/**
 * Card «Protezione margine sul recovery» — Impostazioni → Recovery.
 * Solo presentazione; stato e salvataggio restano nel parent (RecoverySettings).
 */
import { useTranslation } from "react-i18next";
import { Ic } from "../shared";

function RecoveryMarginShieldIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RailIconTag() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function RailIconCoupon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="8" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M7 8V6a2 2 0 012-2h6a2 2 0 012 2v2" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M3 12h18" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function RailIconLock() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CalloutLayersIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 8l9 6 9-6M3 16l9 6 9-6" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M3 12l9 6 9-6" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  );
}

function DetailsInfoIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Freccia minimale (stroke, niente riempimento) — usata solo nel summary della guida collassabile. */
function GuideExpandChevron() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function parseDisplayPct(str) {
  const raw = String(str ?? "").trim().replace(",", ".");
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return 15;
  return Math.min(100, Math.max(0, n));
}

/**
 * Guida in `<details>` chiusa di default — mock `/root/docs/carthub-recovery-margin-guide-layout-mockup.html`.
 */
function RecoveryMarginGuideExplainer() {
  const { t } = useTranslation("settings");
  return (
    <details className="ch-margin-guide-details">
      <summary className="ch-margin-guide-details__summary">
        <span className="ch-margin-guide-details__summary-text">{t("margin.guideSummary")}</span>
        <span className="ch-margin-guide-details__chev" aria-hidden="true">
          <GuideExpandChevron />
        </span>
      </summary>

      <div className="ch-margin-guide-details__panel">
        <p className="ch-margin-guide__lead" id="margin-guide-lead">
          {t("margin.guideLead")}
        </p>

        <div className="ch-margin-guide__step">
        <div className="ch-margin-guide__step-main">
          <h3 className="ch-margin-guide__step-title">{t("margin.scenarioStartTitle")}</h3>
          <div className="ch-margin-guide__step-body">
            <p>{t("margin.scenarioStartP")}</p>
            <ul className="ch-margin-guide__list">
              <li>
                <strong>{t("margin.itemShoes")}</strong> → {t("margin.itemShoesPrice")}
              </li>
              <li>
                <strong>{t("margin.itemBelt")}</strong> → {t("margin.itemBeltDetail")}
              </li>
              <li>
                <strong>{t("margin.itemSweater")}</strong> → {t("margin.itemSweaterPrice")}
              </li>
            </ul>
          </div>
        </div>
        </div>

        <div className="ch-margin-guide__step">
        <div className="ch-margin-guide__step-main">
          <h3 className="ch-margin-guide__step-title">{t("margin.budgetTitle")}</h3>
          <div className="ch-margin-guide__step-body">
            <p>{t("margin.budgetP1")}</p>
            <div className="ch-margin-guide__formula" aria-label={t("margin.budgetTitle")}>
              {t("margin.budgetFormula")}
            </div>
            <p>{t("margin.budgetP2")}</p>
          </div>
        </div>
        </div>

        <div className="ch-margin-guide__step">
        <div className="ch-margin-guide__step-main">
          <h3 className="ch-margin-guide__step-title">{t("margin.toggleTitle")}</h3>
          <div className="ch-margin-guide__step-body">
            <p>{t("margin.toggleP1")}</p>
            <p>{t("margin.toggleP2")}</p>
          </div>
        </div>
        </div>

        <div className="ch-margin-guide__step">
        <div className="ch-margin-guide__step-main">
          <h3 className="ch-margin-guide__step-title">{t("margin.compareTitle")}</h3>
          <div className="ch-margin-guide__compare">
            <article className="ch-margin-guide__pill">
              <h4 className="ch-margin-guide__pill-title">{t("margin.pillBudgetTitle")}</h4>
              <dl className="ch-margin-guide__pill-dl">
                <dt>{t("margin.pillBudgetWhat")}</dt>
                <dd>{t("margin.pillBudgetWhatDd")}</dd>
                <dt>{t("margin.pillBudgetWhen")}</dt>
                <dd>{t("margin.pillBudgetWhenDd")}</dd>
              </dl>
            </article>
            <article className="ch-margin-guide__pill">
              <h4 className="ch-margin-guide__pill-title">{t("margin.pillPromoTitle")}</h4>
              <dl className="ch-margin-guide__pill-dl">
                <dt>{t("margin.pillBudgetWhat")}</dt>
                <dd>{t("margin.pillPromoWhatDd")}</dd>
                <dt>{t("margin.pillBudgetWhen")}</dt>
                <dd>{t("margin.pillPromoWhenDd")}</dd>
              </dl>
            </article>
          </div>
        </div>
        </div>

        <p className="ch-margin-guide__foot">{t("margin.guideFoot")}</p>
      </div>
    </details>
  );
}

export function RecoveryMarginProtectionCard({
  blockLinePromo,
  setBlockLinePromo,
  linePromoThreshStr,
  setLinePromoThreshStr,
  blockCartCouponSnap,
  setBlockCartCouponSnap,
  couponNoStack,
  setCouponNoStack,
  marginErr,
  marginSaving,
  marginSaved,
  onSave,
}) {
  const { t } = useTranslation("settings");
  const displayPct = parseDisplayPct(linePromoThreshStr);

  return (
    <div className="ch-card ch-help-legend ch-help-legend--embed ch-margin-card" style={{ marginBottom: 16 }}>
      <h2 className="ch-margin-card__title">
        <RecoveryMarginShieldIcon />
        {t("margin.title")}
      </h2>
      <p className="ch-margin-card__intro">{t("margin.intro")}</p>

      <RecoveryMarginGuideExplainer />

      <div className="ch-margin-row">
        <div className="ch-margin-row__rail" aria-hidden="true">
          <RailIconTag />
        </div>
        <div className="ch-margin-row__body">
          <div className="ch-margin-row__label-row">
            <label className="ch-toggle" htmlFor="margin-recovery-block-line-promo">
              <input
                id="margin-recovery-block-line-promo"
                type="checkbox"
                checked={blockLinePromo}
                onChange={(e) => setBlockLinePromo(e.target.checked)}
              />
              <span className="ch-toggle-track" />
            </label>
            <label className="ch-margin-row__title-label" htmlFor="margin-recovery-block-line-promo">
              {t("margin.row1Title")}
            </label>
          </div>
          <p className="ch-hint ch-margin-row__hint">
            {t("margin.row1Hint")}
          </p>
          {blockLinePromo && (
            <div className="ch-margin-threshold">
              <label className="ch-margin-threshold__label" htmlFor="margin-line-promo-threshold">
                {t("margin.thresholdLabel")}
              </label>
              <div className="ch-margin-threshold__row">
                <div className="ch-margin-threshold__track" aria-hidden="true" />
                <input
                  id="margin-line-promo-threshold"
                  type="number"
                  min={0}
                  max={100}
                  className="ch-input ch-input-sm"
                  style={{ maxWidth: 90 }}
                  value={linePromoThreshStr}
                  onChange={(e) => setLinePromoThreshStr(e.target.value)}
                  aria-describedby="margin-threshold-hint"
                />
              </div>
              <p id="margin-threshold-hint" className="ch-hint ch-margin-threshold__hint">
                {t("margin.thresholdExample", { pct: displayPct })}
              </p>
            </div>
          )}
          <div className="ch-margin-scenario">
            <span className="ch-margin-scenario__tag">{t("margin.exampleTag")}</span>
            <p>{t("margin.scenarioMarco")}</p>
          </div>
        </div>
      </div>

      <div className="ch-margin-row">
        <div className="ch-margin-row__rail" aria-hidden="true">
          <RailIconCoupon />
        </div>
        <div className="ch-margin-row__body">
          <div className="ch-margin-row__label-row">
            <label className="ch-toggle" htmlFor="margin-recovery-block-cart-coupon">
              <input
                id="margin-recovery-block-cart-coupon"
                type="checkbox"
                checked={blockCartCouponSnap}
                onChange={(e) => setBlockCartCouponSnap(e.target.checked)}
              />
              <span className="ch-toggle-track" />
            </label>
            <label className="ch-margin-row__title-label" htmlFor="margin-recovery-block-cart-coupon">
              {t("margin.row2Title")}
            </label>
          </div>
          <p className="ch-hint ch-margin-row__hint">
            {t("margin.row2Hint")}
          </p>
          <div className="ch-margin-scenario">
            <span className="ch-margin-scenario__tag">{t("margin.exampleTag")}</span>
            <p>{t("margin.scenarioLauraCoupon")}</p>
          </div>
        </div>
      </div>

      <div className="ch-margin-row">
        <div className="ch-margin-row__rail" aria-hidden="true">
          <RailIconLock />
        </div>
        <div className="ch-margin-row__body">
          <div className="ch-margin-row__label-row">
            <label className="ch-toggle" htmlFor="margin-recovery-coupon-no-stack">
              <input
                id="margin-recovery-coupon-no-stack"
                type="checkbox"
                checked={couponNoStack}
                onChange={(e) => setCouponNoStack(e.target.checked)}
              />
              <span className="ch-toggle-track" />
            </label>
            <label className="ch-margin-row__title-label" htmlFor="margin-recovery-coupon-no-stack">
              {t("margin.row3Title")}
            </label>
          </div>
          <p className="ch-hint ch-margin-row__hint">
            {t("margin.row3Hint")}
          </p>
          <div className="ch-margin-scenario">
            <span className="ch-margin-scenario__tag">{t("margin.exampleTag")}</span>
            <p>{t("margin.scenarioLauraStack")}</p>
          </div>
        </div>
      </div>

      <details className="ch-margin-details">
        <summary className="ch-margin-details__summary">
          <DetailsInfoIcon />
          {t("margin.mixedTitle")}
        </summary>
        <div className="ch-margin-details__body">
          <p>{t("margin.mixedBody", { pct: displayPct })}</p>
        </div>
      </details>

      <div className="ch-margin-callout">
        <CalloutLayersIcon />
        <div>
          <strong>{t("margin.calloutTitle")}</strong>{" "}
          {t("margin.calloutBody")}
        </div>
      </div>

      <p className="ch-margin-budget">
        {t("margin.budgetFoot")}
      </p>

      {marginErr && (
        <div className="ch-info" style={{ background: "#fff5f5", border: "1px solid #ffcdd2", color: "#c62828", marginBottom: 10 }}>
          {marginErr}
        </div>
      )}
      <div className="ch-actions">
        <button type="button" className="ch-btn ch-btn-primary" onClick={onSave} disabled={marginSaving}>
          {marginSaving ? t("margin.saving") : marginSaved ? <>{Ic.check} {t("margin.saved")}</> : t("margin.save")}
        </button>
      </div>
    </div>
  );
}
